import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COLORS, DEFAULT_THEME, THEME_DATA } from '../constants';
import useOnClickOutside from '../hooks/use-onclick-outside';
import { ChevronIcon, CloseIcon } from './Icons';
import Options from './Options';
import SearchInput from './SearchInput';
import SelectProvider from './SelectProvider';
import Spinner from './Spinner';

const Select = ({
  options = [],
  value = null,
  onChange,
  onSearchInputChange,
  placeholder = 'Select...',
  searchInputPlaceholder = 'Search...',
  isMultiple = false,
  isClearable = false,
  isHidden = false,
  isSearchable = false,
  isDisabled = false,
  loading = false,
  menuIsOpen = false,
  menuPortalTarget = typeof document === 'undefined' ? null : document.body,
  preservePortalMenuInteraction = true,
  menuWidth = null,
  menuTitle = '',
  styles = {},
  noOptionsMessage = 'No options found',
  primaryColor = DEFAULT_THEME,
  inputId,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  formatGroupLabel = null,
  formatOptionLabel = null,
  classNames = {
    menuButton: ({ isDisabled }) =>
      `flex text-sm p-1 text-gray-900 border border-gray-400 rounded-md shadow-sm transition-all duration-300 focus:outline-primary ${
        isDisabled
          ? 'bg-gray-200'
          : 'bg-white hover:border-gray-400 focus:border-primary focus:ring focus:ring-primary/20'
      }`,
    list: 'flex flex-col gap-y-1.5 max-h-48 overflow-auto',
  },
  onKeyDownSearch = () => {},
}) => {
  const [open, setOpen] = useState(menuIsOpen);
  const [list, setList] = useState(options);
  const [inputValue, setInputValue] = useState('');
  const ref = useRef(null);
  const searchBoxRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => {
    const formatItem = (item) => {
      if ('disabled' in item) return item;
      return {
        ...item,
        disabled: false,
      };
    };

    setList(
      (Array.isArray(options) ? options : []).map((item) => {
        if ('options' in item) {
          return {
            label: item.label,
            options: item.options.map(formatItem),
          };
        } else {
          return formatItem(item);
        }
      })
    );
  }, [options]);

  useEffect(() => {
    if (isSearchable) {
      if (open) {
        searchBoxRef.current?.select();
      } else {
        setInputValue('');
      }
    }
  }, [open, isSearchable]);

  const updateMenuPosition = useCallback(() => {
    if (!menuPortalTarget || !ref.current || typeof window === 'undefined') return;

    const rect = ref.current.getBoundingClientRect();
    const viewportPadding = 8;
    const gap = 6;
    const availableBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
    const availableAbove = rect.top - gap - viewportPadding;
    const opensAbove = availableBelow < 240 && availableAbove > availableBelow;
    const availableHeight = opensAbove ? availableAbove : availableBelow;
    const maxWidth = Math.max(0, window.innerWidth - viewportPadding * 2);
    const requestedWidth = Number.isFinite(menuWidth)
      ? Math.max(rect.width, menuWidth)
      : rect.width;
    const width = Math.min(requestedWidth, maxWidth);
    const left = Math.max(
      viewportPadding,
      Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - viewportPadding)
    );

    setMenuPosition({
      left,
      width,
      maxHeight: Math.max(120, Math.min(440, availableHeight)),
      ...(opensAbove
        ? { bottom: window.innerHeight - rect.top + gap, top: 'auto' }
        : { top: rect.bottom + gap, bottom: 'auto' }),
    });
  }, [menuPortalTarget, menuWidth]);

  const toggle = useCallback(() => {
    if (!isDisabled) {
      if (!open) updateMenuPosition();
      setOpen(!open);
    }
  }, [isDisabled, open, updateMenuPosition]);

  useEffect(() => {
    if (!open || !menuPortalTarget || typeof window === 'undefined') return undefined;

    updateMenuPosition();

    const handleScroll = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('wheel', handleScroll, { capture: true, passive: true });
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('wheel', handleScroll, true);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [menuPortalTarget, open, updateMenuPosition]);

  const closeDropDown = useCallback(() => {
    if (open) setOpen(false);
  }, [open]);

  useOnClickOutside(
    ref,
    closeDropDown,
    preservePortalMenuInteraction && menuPortalTarget ? menuRef : null
  );

  const onPressEnterOrSpace = useCallback(
    (e) => {
      e.preventDefault();
      if ((e.code === 'Enter' || e.code === 'Space') && !isDisabled) {
        toggle();
      }
    },
    [isDisabled, toggle]
  );

  const onPressEnterOrSpaceSearch = useCallback(() => {
    onKeyDownSearch();
  }, [isDisabled]);

  const handleValueChange = useCallback(
    (selected) => {
      const deselect = () => {
        onChange(null);
        if (!isMultiple) closeDropDown();
      };

      if (!isMultiple) {
        if (value && selected.value === value.value) {
          deselect();
        } else {
          onChange(selected);
          closeDropDown();
        }
      } else {
        const isAlreadySelected =
          Array.isArray(value) && value.some((v) => v.value === selected.value);

        if (isAlreadySelected) {
          const newValue = value.filter((v) => v.value !== selected.value);
          onChange(newValue.length > 0 ? newValue : null);
        } else {
          onChange(value === null ? [selected] : [...value, selected]);
        }
      }
    },
    [closeDropDown, isDisabled, isMultiple, onChange, value]
  );

  const clearValue = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(null);
    },
    [onChange]
  );

  const removeItem = useCallback(
    (e, item) => {
      if (isMultiple && Array.isArray(value) && value.length) {
        e.stopPropagation();
        const result = value.filter((current) => item.value !== current.value);
        onChange(result.length ? result : null);
      }
    },
    [isMultiple, onChange, value]
  );

  const getSelectClass = useCallback(() => {
    let ringColor = THEME_DATA.ring[DEFAULT_THEME];
    if (COLORS.includes(primaryColor)) {
      ringColor = THEME_DATA.ring[primaryColor];
    }

    let borderFocus = THEME_DATA.borderFocus[DEFAULT_THEME];
    if (COLORS.includes(primaryColor)) {
      borderFocus = THEME_DATA.borderFocus[primaryColor];
    }
    const baseClass =
      'flex text-sm flex-wrap justify-between relative items-center text-gray-500 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none';
    const defaultClass = `${baseClass} ${
      isDisabled
        ? 'bg-gray-200'
        : `bg-white hover:border-gray-400 ${borderFocus} focus:ring ${ringColor}`
    }`;

    return classNames && classNames.menuButton
      ? classNames.menuButton({ isDisabled })
      : defaultClass;
  }, [classNames, isDisabled, primaryColor]);

  const getTagItemClass = useCallback(
    (item) => {
      const baseClasse = 'bg-gray-200 border rounded-sm flex space-x-1';
      const disabledClass = isDisabled ? 'border-gray-500 px-1' : 'pl-1';
      return classNames?.tagItem
        ? classNames.tagItem({ item, isDisabled })
        : `${baseClasse} ${disabledClass}`;
    },
    [classNames, isDisabled]
  );
  const isEmptyValue =
    !value ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && value.value === '');

  const basePortalStyle =
    menuPortalTarget && menuPosition
      ? {
          position: 'fixed',
          left: menuPosition.left,
          top: menuPosition.top,
          bottom: menuPosition.bottom,
          width: menuPosition.width,
          maxHeight: menuPosition.maxHeight,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: 'hidden',
          overscrollBehavior: 'contain',
          marginTop: 0,
          transform: 'none',
          zIndex: 9999,
        }
      : undefined;

  const portalStyle =
    basePortalStyle && typeof styles.menuPortal === 'function'
      ? styles.menuPortal(basePortalStyle)
      : basePortalStyle;

  const renderMenu = () => (
    <div
      ref={menuRef}
      className={
        classNames?.menu
          ? classNames.menu
          : 'absolute z-10 mt-1.5 w-full min-w-[250px] rounded border bg-white py-1 text-sm text-gray-700 shadow-lg'
      }
      style={portalStyle}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
      onScroll={(event) => event.stopPropagation()}
    >
      {menuTitle && (
        <div className='mb-2 flex flex-none items-start gap-2 rounded-lg border border-primary/15 bg-primary/5 px-3 py-2.5 text-xs leading-5 text-slate-700'>
          <span className='mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white'>
            i
          </span>
          <span className='min-w-0 break-words font-semibold'>{menuTitle}</span>
        </div>
      )}

      {isSearchable && (
        <SearchInput
          ref={searchBoxRef}
          value={inputValue}
          placeholder={searchInputPlaceholder}
          onKeyDownSearch={onPressEnterOrSpaceSearch}
          onChange={(e) => {
            if (onSearchInputChange && typeof onSearchInputChange === 'function')
              onSearchInputChange(e);
            setInputValue(e.target.value);
          }}
        />
      )}

      <Options
        id={inputId ? `${inputId}-listbox` : undefined}
        list={list}
        noOptionsMessage={noOptionsMessage}
        text={inputValue}
        isMultiple={isMultiple}
        value={value}
        primaryColor={primaryColor || DEFAULT_THEME}
      />
    </div>
  );

  return (
    <SelectProvider
      otherData={{
        formatGroupLabel,
        formatOptionLabel,
        classNames,
      }}
      value={value}
      handleValueChange={handleValueChange}
    >
      <div className='relative w-full' ref={ref}>
        <div
          id={inputId}
          tabIndex={isDisabled ? -1 : 0}
          role='combobox'
          aria-disabled={isDisabled}
          aria-label={ariaLabel || (typeof placeholder === 'string' ? placeholder : undefined)}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-expanded={open}
          aria-haspopup='listbox'
          aria-controls={inputId ? `${inputId}-listbox` : undefined}
          onKeyDown={onPressEnterOrSpace}
          onClick={toggle}
          className={
            getSelectClass() +
            (ariaInvalid ? ' border-red-500 focus:border-red-500 focus:ring-red-200' : '')
          }
        >
          <div className='relative grid flex-1 items-center overflow-hidden py-2 pl-2.5 pr-2'>
            {!isMultiple ? (
              <p className='cursor-default select-none truncate'>
                {!isEmptyValue && value && !Array.isArray(value) ? value.label : placeholder}
              </p>
            ) : (
              <>
                {isEmptyValue && placeholder}

                {Array.isArray(value) &&
                  value.map((item, index) => (
                    <div className={getTagItemClass(item)} key={index}>
                      <p
                        className={
                          classNames?.tagItemText
                            ? classNames.tagItemText
                            : 'cursor-default select-none truncate text-gray-600'
                        }
                      >
                        {item.label}
                      </p>
                      {!isDisabled && (
                        <div
                          role='button'
                          tabIndex={0}
                          onClick={(e) => removeItem(e, item)}
                          className={
                            classNames?.tagItemIconContainer
                              ? classNames.tagItemIconContainer
                              : 'flex cursor-pointer items-center rounded-r-sm px-1 hover:bg-red-200 hover:text-red-600'
                          }
                        >
                          <CloseIcon
                            className={
                              classNames?.tagItemIcon ? classNames.tagItemIcon : 'mt-0.5 h-3 w-3'
                            }
                          />
                        </div>
                      )}
                    </div>
                  ))}
              </>
            )}
          </div>

          <div className='flex flex-none items-center py-1.5'>
            {loading && (
              <div className='px-1.5'>
                <Spinner primaryColor={primaryColor} />
              </div>
            )}

            {isClearable && !isDisabled && !isEmptyValue && (
              <div className='cursor-pointer px-1.5' onClick={clearValue}>
                <CloseIcon
                  className={classNames?.closeIcon ? classNames.closeIcon : 'h-5 w-5 p-0.5'}
                />
              </div>
            )}

            <div className='h-full'>
              <span className='inline-block h-full w-px bg-gray-300 text-white/0' />
            </div>

            {isHidden !== true && (
              <div className='px-1.5'>
                <ChevronIcon
                  className={`h-6 w-6 transition duration-300 p-0.5${
                    open ? ' rotate-90 transform text-gray-500' : ' text-gray-300'
                  }`}
                />
              </div>
            )}
          </div>
        </div>

        {open &&
          !isDisabled &&
          (menuPortalTarget && menuPosition
            ? createPortal(renderMenu(), menuPortalTarget)
            : renderMenu())}
      </div>
    </SelectProvider>
  );
};

export default Select;
